import React, { useCallback, useEffect, useState } from 'react';
import {
	ChangeSet,
	ChangeSetEntityChangeType,
	DataTypeEntityOption,
	DataTypeState,
	EntityId,
	EntityType,
	PrimitiveTypes,
	ProjectState,
	ValueDescriptorState,
	READABLE_ENTITY_TYPES,
	EntityInstanceErrorCode,
	lowercaseFirstLetter,
	StateMutationErrorCode,
	toSentenceCase,
	translateLogicErrorCodeShort,
	getRecursiveParentsIds,
	StateMutationAction,
} from '@elyx-code/project-logic-tree';
import {
	Alert,
	TextField,
	Tooltip as MuiTooltip,
	Collapse,
} from '@mui/material';
import CheckboxWithLabel from '../../../lib/checkbox/CheckboxWithLabel';
import { EditorService } from '../../../services/editor';
import DataTypeBuilder, {
	AllowedDataTypeListTypes,
	ILiteralValueOption,
} from '../../data-type/DataTypeBuilder';
import Tooltip from '../../../lib/tooltip/Tooltip';
import Button from '../../../lib/button/Button';
import { useParams } from 'react-router-dom';
import EntityIcon from '../../entity-icon/EntityIcon';
import EntityDialogSectionHeader from '../../EntityDialogSectionHeader';
import DynamicValueEntry from '../../dynamic-value/DynamicValueEntry';
import WarningIcon from '../../../assets/warning-sign-24px.png';
import { DataTypeBuilderOption } from '../../../services/editor/utils';
import { debounce } from '../../../utils';

export interface IValueDescriptorProps {
	project: EditorService;
	entity?: ValueDescriptorState;
	onClose?: (e: any) => void;
}

export enum ValueDescriptorErrorEnum {
	NameRequired = 'name-required',
	IncompleteDataType = 'incomplete-data-type',
}

export type ValueDescriptorError = ValueDescriptorErrorEnum | string;

export function fromValueDescriptorErrorToReadableMessage(
	error: ValueDescriptorError | string,
): string {
	switch (error) {
		case ValueDescriptorErrorEnum.NameRequired:
			return 'Name is required';
		case ValueDescriptorErrorEnum.IncompleteDataType:
			return 'The composition of the data type is incomplete. Please fill in all required fields on each data type element.';
		default:
			return toSentenceCase(error);
	}
}

function calculateValueDescriptorCreationFormErrors(
	entity: ValueDescriptorState,
): (ValueDescriptorError | string)[] {
	const errors: (ValueDescriptorError | string)[] = [];

	if (!entity.name) {
		errors.push(ValueDescriptorErrorEnum.NameRequired);
	}

	if (!!entity.dataType) {
		if (!entity.dataType.isResolved) {
			errors.push(ValueDescriptorErrorEnum.IncompleteDataType);
		}

		const flatErrors = (
			entity.dataType.toFlat().filter((child) => {
				if (child.type === EntityType.DataType) {
					if (child.id === entity.dataType.id) {
						// This is the root data type, skip
						return true;
					}

					const allParentsOfNestedDT = getRecursiveParentsIds(child);

					if (allParentsOfNestedDT.includes(entity.dataType.id)) {
						// This is a nested data type that creates a circular reference
						return true;
					} else {
						return false;
					}
				}

				return false;
			}) as DataTypeState[]
		).flatMap((dt) => dt.getShallowErrors());

		if (flatErrors.length > 0) {
			errors.push(
				...flatErrors.map((err) => translateLogicErrorCodeShort(err)),
			);
		}
	}

	return errors;
}

// Not all entity types can have inputs with any data type
// Sometimes, like in the case of a search, the data type of its inputs can only be a primitive type or a list of primitives
// This function, given an option, will return some metadata that will help it render the option in the data type builder
export function resolveDataTypeOptionConfigForValueDescriptor(
	option: DataTypeBuilderOption | ILiteralValueOption,
	input: ValueDescriptorState,
): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	if (input.parent.type === EntityType.Search) {
		if (
			option?.type !== EntityType.PrimitiveEntity ||
			[
				PrimitiveTypes.KeyValue,
				PrimitiveTypes.Untyped,
				PrimitiveTypes.ActionDescriptor,
				PrimitiveTypes.EntityTemplate,
			].includes(option?.name as PrimitiveTypes)
		) {
			return {
				disabled: true,
				tooltip:
					'Search statements can only have primitive data types as inputs',
			};
		}
	}

	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeAsTypeConfigForValueDescriptor(
	input: ValueDescriptorState,
): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	if (input.parent.type === EntityType.Search) {
		return {
			disabled: true,
			tooltip:
				'Search statements cannot have template values as inputs, only instances of primitive data types',
		};
	}

	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeLikeConfigForValueDescriptor(
	input: ValueDescriptorState,
): {
	disabled: boolean;
	tooltip: React.ReactNode;
} {
	if (input.parent.type === EntityType.Search) {
		return {
			disabled: true,
			tooltip: 'Search statements can only have primitive data types as inputs',
		};
	}

	return {
		disabled: false,
		tooltip: null,
	};
}

export function resolveDataTypeAsListConfigForValueDescriptor(
	input: ValueDescriptorState,
): {
	allowed: AllowedDataTypeListTypes;
	tooltip: React.ReactNode;
} {
	if (input.parent.type === EntityType.Search) {
		return {
			allowed: AllowedDataTypeListTypes.Flat,
			tooltip:
				'Search statements can only have a single flat level of list values for inputs',
		};
	}

	return {
		allowed: AllowedDataTypeListTypes.All,
		tooltip: null,
	};
}

const ValueDescriptor: React.FC<IValueDescriptorProps> = ({
	project,
	entity: _entity,
	onClose,
}) => {
	const [updateKey, setUpdateKey] = useState(0);
	const { entityId } = useParams<{
		entityType: EntityType;
		entityId: EntityId;
	}>();

	const entity: ValueDescriptorState | null =
		_entity ||
		(project?.logic?.get(entityId) as ValueDescriptorState | null) ||
		null;

	const [dataType, _setDataType] = useState<DataTypeState | null>(
		entity?.dataType || null,
	);
	const [entityName, setEntityName] = useState<string>(entity.name);
	const [entityDescription, setEntityDescription] = useState<string>(
		entity.description,
	);

	const [isRequired, _setIsRequired] = useState<boolean>(entity.required);

	const [metadataSectionOpen, setMetadataSectionOpen] = useState(true);
	const [dataTypeSectionOpen, setDataTypeSectionOpen] = useState(false);
	const [defaultValueSectionOpen, setDefaultValueSectionOpen] = useState(false);
	// const [baseFunctionalitySectionOpen, setBaseFunctionalitySectionOpen] =
	// 	useState(false);
	const [interactivitySectionOpen, setInteractivitySectionOpen] =
		useState(false);

	const setIsRequired = useCallback((value: boolean) => {
		_setIsRequired(value);

		let actionName = value
			? StateMutationAction.MakeValueDescriptorRequired
			: StateMutationAction.MakeValueDescriptorNonRequired;

		if (entity.parent?.type === EntityType.Search) {
			actionName = value
				? StateMutationAction.MakeSearchInputRequired
				: StateMutationAction.MakeSearchInputNonRequired;
		}

		const changeSet = project?.logic?.addChangeSet(
			new ChangeSet(
				project?.logic,
				ProjectState.sessionAuthor,
				new Date().toISOString(),
				entity,
				false,
				actionName,
			),
		);

		entity?.metaSync({ required: value }, changeSet);

		entity?.afterAllChildrenInitialized(changeSet);

		project.renderAndCloseChangeSet(changeSet);
	}, []);

	const debouncedOnChangeName = useCallback(
		debounce(
			(value: string) => {
				let actionName = StateMutationAction.RenameValueDescriptor;

				if (entity.parent?.type === EntityType.Search) {
					actionName = StateMutationAction.RenameSearchInput;
				}
				const changeSet = project?.logic?.addChangeSet(
					new ChangeSet(
						project?.logic,
						ProjectState.sessionAuthor,
						new Date().toISOString(),
						entity,
						false,
						actionName,
					),
				);

				setUpdateKey((prev) => prev + 1);
				entity?.metaSync({ name: value || null }, changeSet);

				project.renderAndCloseChangeSet(changeSet);
				if (changeSet.has(entity.id)) {
					project.emit(entity.id, {});
				}
			},
			1500,
			false,
		),
		[],
	);

	const debouncedOnChangeDescription = useCallback(
		debounce(
			(value: string) => {
				let actionName = StateMutationAction.ChangeValueDescriptorDescription;

				if (entity.parent?.type === EntityType.Search) {
					actionName = StateMutationAction.ChangeSearchInputDescription;
				}

				const changeSet = project?.logic?.addChangeSet(
					new ChangeSet(
						project?.logic,
						ProjectState.sessionAuthor,
						new Date().toISOString(),
						entity,
						false,
						actionName,
					),
				);

				setUpdateKey((prev) => prev + 1);
				entity?.metaSync({ description: value || null }, changeSet);

				project.renderAndCloseChangeSet(changeSet);
				if (changeSet.has(entity.id)) {
					project.emit(entity.id, {});
				}
			},
			600,
			false,
		),
		[],
	);

	const [errors, setErrors] = useState<ValueDescriptorError[]>(
		calculateValueDescriptorCreationFormErrors(entity),
	);

	const setDataType = (
		newDataTypeEntity: DataTypeState | null,
		changeSet: ChangeSet | null,
	) => {
		newDataTypeEntity?.setParent(entity, changeSet);
		entity?.setDataType(newDataTypeEntity, changeSet);

		changeSet?.add(entity, ChangeSetEntityChangeType.Affected);

		_setDataType(newDataTypeEntity);
		setUpdateKey((prev) => prev + 1);

		project.renderAndCloseChangeSet(changeSet);
	};

	useEffect(() => {
		const newErrors = calculateValueDescriptorCreationFormErrors(entity);
		setErrors(newErrors);
		project.emit(entity.parent.id, {});
	}, [entityName, updateKey]);

	return (
		<div
			style={{
				display: 'flex',
				flexGrow: 1,
				height: '100%',
				overflowY: 'scroll',
			}}
		>
			{/* We need the 'span' for the react ref */}
			<span
				style={{
					maxHeight: '100%',
					maxWidth: '100%',
					flexGrow: 1,
					height: '100%',
				}}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						gap: '4px',
						justifyContent: 'space-between',
					}}
				>
					<span
						style={{
							maxWidth: '100%',
							maxHeight: '100%',
							overflowY: 'scroll',
							display: 'flex',
							flexDirection: 'column',
							height: '100%',
						}}
					>
						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: metadataSectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title='Name and description'
								subtitle={`Add a name and description for this ${READABLE_ENTITY_TYPES[
									entity.type
								].singular.toLowerCase()}`}
								hideShowSuffix='the name and description section'
								open={metadataSectionOpen}
								onToggle={() => setMetadataSectionOpen(!metadataSectionOpen)}
							/>

							<Collapse in={metadataSectionOpen}>
								<div
									style={{
										marginTop: 6,
										display: 'flex',
										flexDirection: 'column',
										gap: 12,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<TextField
										label='Input name'
										variant='outlined'
										disabled={!entity.editable}
										style={{ width: '50%' }}
										size='small'
										value={entityName || ''}
										onChange={(e) => {
											setEntityName(e.target.value);
											debouncedOnChangeName(e.target.value);
										}}
									/>

									<TextField
										label='Input description'
										variant='outlined'
										fullWidth
										disabled={!entity.editable}
										multiline
										minRows={3}
										size='small'
										value={entityDescription || ''}
										onChange={(e) => {
											setEntityDescription(e.target.value);
											debouncedOnChangeDescription(e.target.value);
										}}
									/>
								</div>
							</Collapse>
						</section>

						<hr
							style={{
								margin: '1px 0',
								border: '1px solid rgba(0,0,0,0.08)',
							}}
						/>

						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: dataTypeSectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title='Data type'
								subtitle='Select the type of data that this input will hold'
								hideShowSuffix='the data type section'
								open={dataTypeSectionOpen}
								onToggle={() => setDataTypeSectionOpen(!dataTypeSectionOpen)}
								headerStartIcon={
									<EntityIcon entity={entity} project={project} />
								}
							/>

							<Collapse in={dataTypeSectionOpen}>
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: 20,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<DataTypeBuilder
										project={project}
										topParent={entity}
										dataType={dataType}
										baseDataTypes={[]}
										readOnly={!entity.editable}
										onChangeSet={() => {
											const newChangeSet = project.logic.addChangeSet(
												new ChangeSet(
													project.logic,
													ProjectState.sessionAuthor,
													new Date().toISOString(),
													entity,
													false,
													StateMutationAction.ChangeValueDescriptorDataType,
												),
											);

											return newChangeSet;
										}}
										onChange={(newDataTypeEntity, changeSet) => {
											setDataType(newDataTypeEntity, changeSet);
										}}
										lists={
											resolveDataTypeAsListConfigForValueDescriptor(entity)
												.allowed
										}
										getOptionDisabled={(option) =>
											resolveDataTypeOptionConfigForValueDescriptor(
												option,
												entity,
											).disabled
										}
										getListDisabledTooltip={(option) =>
											resolveDataTypeAsListConfigForValueDescriptor(entity)
												.tooltip
										}
										getOptionTooltip={(option) =>
											resolveDataTypeOptionConfigForValueDescriptor(
												option,
												entity,
											).tooltip
										}
										disallowAsType={
											resolveDataTypeAsTypeConfigForValueDescriptor(entity)
												.disabled
										}
										getAsTypeDisabledTooltip={() =>
											resolveDataTypeAsTypeConfigForValueDescriptor(entity)
												.tooltip
										}
										disallowLike={
											resolveDataTypeLikeConfigForValueDescriptor(entity)
												.disabled
										}
										getLikeDisabledTooltip={() =>
											resolveDataTypeLikeConfigForValueDescriptor(entity)
												.tooltip
										}
									/>
								</div>
							</Collapse>
						</section>

						<hr
							style={{
								margin: '1px 0',
								border: '1px solid rgba(0,0,0,0.08)',
							}}
						/>

						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: defaultValueSectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title='Default value'
								subtitle='The initial value, if no other is given when the input is first used'
								hideShowSuffix='the default value section'
								open={defaultValueSectionOpen}
								onToggle={() =>
									setDefaultValueSectionOpen(!defaultValueSectionOpen)
								}
								headerStartIcon={
									!!entity.errors.find(
										(error) =>
											error.code ===
											EntityInstanceErrorCode.MissingRequiredValue,
									) ? (
										<MuiTooltip
											title='Missing required value'
											placement='top'
											arrow
										>
											<img
												style={{ width: 20, height: 20 }}
												id={'logic-error-icon--' + entity.id}
												src={WarningIcon}
												className={'warning-icon logic-error-icon'}
											/>
										</MuiTooltip>
									) : null
								}
							/>

							<Collapse in={defaultValueSectionOpen}>
								<div
									style={{
										marginTop: 6,
										display: 'flex',
										flexDirection: 'column',
										gap: 20,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<DynamicValueEntry
										entity={entity}
										project={project}
										parentDisabledReasons={
											!entity.editable
												? [StateMutationErrorCode.ActionOwnerEntityNotEditable]
												: []
										}
										onlyDefault
									>
										{(extraContent) => extraContent}
									</DynamicValueEntry>
								</div>
							</Collapse>
						</section>

						<hr
							style={{
								margin: '1px 0',
								border: '1px solid rgba(0,0,0,0.08)',
							}}
						/>

						<section
							style={{
								display: 'flex',
								flexDirection: 'column',
								paddingBottom: interactivitySectionOpen ? 20 : 0,
							}}
						>
							<EntityDialogSectionHeader
								title='Interactivity'
								subtitle='Define how this input is used in the logic'
								hideShowSuffix='the interactivity section'
								open={interactivitySectionOpen}
								onToggle={() =>
									setInteractivitySectionOpen(!interactivitySectionOpen)
								}
							/>

							<Collapse in={interactivitySectionOpen}>
								<div
									style={{
										marginTop: 6,
										display: 'flex',
										flexDirection: 'row',
										gap: 12,
										marginLeft: 80,
										paddingRight: 40,
									}}
								>
									<MuiTooltip
										title={
											'When checked, the value of this input must be present in any representation of the parent definition-entity as a value'
										}
										placement='bottom'
										arrow
										enterDelay={false ? 100 : 400}
										leaveDelay={200}
									>
										<CheckboxWithLabel
											mainLabel="Required"
											subLabel={isRequired ? "Value must be present" : "Value is optional"}
											disabled={!entity.editable}
											className='input-declaration-builder--required-checkbox'
											checked={isRequired}
											onChange={(checked) => {
												setIsRequired(checked as boolean);
											}}
										/>
									</MuiTooltip>
								</div>
							</Collapse>
						</section>
					</span>

					{errors.length > 0 && (
						<div
							style={{
								marginTop: 20,
								marginRight: 40,
								marginLeft: 80,
							}}
						>
							<Alert severity='error'>
								{errors.length === 1 ? (
									<span
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 6,
										}}
									>
										<h4
											style={{
												margin: 0,
												display: 'flex',
											}}
										>
											Error:
										</h4>
										<p
											style={{
												margin: 0,
												display: 'flex',
											}}
										>
											{fromValueDescriptorErrorToReadableMessage(errors[0])}
										</p>
									</span>
								) : (
									<>
										<h4
											style={{
												margin: 0,
											}}
										>
											Errors
										</h4>
										<br className='quarter-space' />
										<ul
											style={{
												listStyleType: 'none',
												padding: 0,
											}}
										>
											{errors.map((error, index) => (
												<li
													key={index}
													style={{
														display: 'flex',
														alignItems: 'center',
													}}
												>
													{/* Circle */}
													<span
														style={{
															fontSize: 10,
															marginRight: 5,
														}}
													>
														&#9679;
													</span>
													<p
														style={{
															margin: 0,
															// fontSize: 10,
														}}
													>
														{fromValueDescriptorErrorToReadableMessage(error)}
													</p>
												</li>
											))}
										</ul>
									</>
								)}
							</Alert>
						</div>
					)}

					<div
						style={{
							alignSelf: 'flex-start',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-end',
							width: '100%',
							gap: '16px',
							padding: '0 40px',
						}}
					>
						<Button
							id='input-declaration--done-button'
							onClick={(e: any) => {
								onClose?.(e);
							}}
							color='primary'
							disabled={errors.length > 0}
						>
							Done
						</Button>
						<Tooltip
							anchorQuerySelector='#input-declaration--done-button'
							placement='bottom'
							openDelay={100}
						>
							{errors.length > 0
								? `Resolve the errors to save the ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[entity.type].singular,
									)}`
								: `Save the ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[entity.type].singular,
									)}`}
						</Tooltip>
					</div>
				</div>
			</span>
		</div>
	);
};

export default ValueDescriptor;
